import { useEffect, useRef, useState } from "react";
import Api from "utils/api";
import { AlertActions } from "@evertrue/et-components";

export const useDonorSearchIntegration = ({ initialData } = {}) => {
  const [integration, setIntegration] = useState(initialData || null);
  const [initialLoading, setInitialLoading] = useState(!initialData);
  const [updating, setUpdating] = useState(false);
  const hasFetched = useRef(!!initialData);

  const getIntegration = async ({ isInitial = false } = {}) => {
    if (isInitial) setInitialLoading(true);
    else setUpdating(true);
    try {
      const { data } = await Api.DONOR_SEARCH.INTEGRATION.get({ disableAlerts: true });
      setIntegration(data);
    } catch (e) {
      // e is response.data (the response body). A 404 with no body resolves to
      // null/undefined; a real server error will have a message.
      if (e && e.message) {
        AlertActions.launchToast({ type: "error", message: e.message });
        console.error("Failed to fetch Donor Search integration:", e);
        // On a real error during the initial load, keep integration null so
        // the card stays hidden — there is no valid state to display.
      }
      // Falsy e = 404 / no integration in DB — hide the card silently.
      if (isInitial) setIntegration(null);
      // For subsequent refetches keep the previous integration value so the
      // card does not disappear while an update is in flight.
    } finally {
      if (isInitial) setInitialLoading(false);
      else setUpdating(false);
    }
  };

  const handleIntegrationClick = async () => {
    try {
      await Api.DONOR_SEARCH.INTEGRATION.put({
        data: { enabled: true },
        disableAlerts: true,
      });
      await getIntegration();
    } catch (e) {
      AlertActions.launchToast({
        type: "error",
        message: "Failed to enable Donor Search integration.",
      });
    }
  };

  const handleLogoutClick = async () => {
    try {
      await Api.DONOR_SEARCH.INTEGRATION.put({
        data: { enabled: false },
        disableAlerts: true,
      });
      await getIntegration();
    } catch (e) {
      AlertActions.launchToast({
        type: "error",
        message: "Failed to disable Donor Search integration.",
      });
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    getIntegration({ isInitial: true });
  }, []);

  return {
    exists: !initialLoading && integration !== null,
    state: integration ? (integration.enabled ? "active" : "disabled") : null,
    loading: updating,
    handleIntegrationClick,
    handleLogoutClick,
  };
};

export default useDonorSearchIntegration;
